// Doc-Ref: docs/features/collaborations-current.md
import { useState, useCallback, useEffect, useRef } from "react";
import {
  Building2,
  Network,
  User,
  Search,
  X,
  Loader2,
  Link as LinkIcon
} from "lucide-react";
import { getAuthorProfileSummaryByID, getSuggestSearchAuthors } from "Explore/Services/Author";
import { getInstitutionDetailSummaryByID, getSuggestSearchInstitutions } from "Explore/Services/Institution";
import { getUnitSummaryById, getSuggestSearchUnits } from "Explore/Services/Unit";
import useRecentEntities from "./useRecentEntities";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { cn } from "lib/utils";

const URL_STATUS = { idle: "idle", resolving: "resolving", resolved: "resolved", error: "error" };

const DEFAULT_ALLOWED_TYPES = ["institution", "unit", "researcher"];

const EntitySelector = ({
  selectedEntities = [],
  onEntitiesChange,
  onEntitiesClear,
  onPendingChange,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState(
    allowedTypes.includes("institution") ? "institution" : (allowedTypes[0] || "institution")
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const [urlStatus, setUrlStatus] = useState(URL_STATUS.idle);
  const [urlMessage, setUrlMessage] = useState("");

  const [localSelectedEntities, setLocalSelectedEntities] = useState(selectedEntities);

  useEffect(() => {
    setLocalSelectedEntities(selectedEntities);
  }, [selectedEntities]);

  const hasChanges = JSON.stringify(localSelectedEntities.map(e => e.id).sort()) !== JSON.stringify(selectedEntities.map(e => e.id).sort());

  // Notify parent about pending state changes
  useEffect(() => {
    if (onPendingChange) {
      onPendingChange(hasChanges, localSelectedEntities);
    }
  }, [hasChanges, localSelectedEntities, onPendingChange]);

  // Called by FiltersRow's Run Analysis button via parent
  const applyChanges = () => {
    onEntitiesChange(localSelectedEntities);
    if (localSelectedEntities.length === 0 && onEntitiesClear) {
      onEntitiesClear();
    }
  };

  const [searchResults, setSearchResults] = useState({
    institutions: [],
    units: [],
    researchers: []
  });

  const searchContainerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const { recentEntities, addRecentEntity } = useRecentEntities();

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizeEntity = useCallback((raw, type) => {
    return {
      ...raw,
      id: raw.id,
      name: raw.name,
      type,
    };
  }, []);

  const handleEntitySelect = useCallback((entity) => {
    setIsDropdownOpen(false);
    setSearchValue("");
    setUrlStatus(URL_STATUS.idle);
    setUrlMessage("");

    // Check if already selected
    if (localSelectedEntities.some(e => e.id === entity.id)) {
      return;
    }

    const handleApiResult = (data, resolvedType) => {
      const fullEntity = { ...entity, ...data, id: entity.id, type: resolvedType };
      setLocalSelectedEntities(prev => [...prev, fullEntity]);
      addRecentEntity(fullEntity);
    };

    if (entity.type === "researcher") {
      getAuthorProfileSummaryByID(entity.id, (data) => handleApiResult(data, "researcher"));
    } else if (entity.type === "institution") {
      getInstitutionDetailSummaryByID(entity.id, (data) => handleApiResult(data, "institution"));
    } else if (entity.type === "unit") {
      getUnitSummaryById(entity.id).then((data) => handleApiResult(data, "unit"));
    } else {
      setLocalSelectedEntities(prev => [...prev, entity]);
      addRecentEntity(entity);
    }
  }, [localSelectedEntities, addRecentEntity]);

  const handleUrlResolve = useCallback((url) => {
    if (!url.trim()) return;

    let entityId = null;
    let type = null;

    try {
      const parsedUrl = new URL(url);
      const pathParts = parsedUrl.pathname.split("/").filter(Boolean);

      if (pathParts.length >= 2) {
        entityId = pathParts[pathParts.length - 1];
        const typeStr = pathParts[pathParts.length - 2];

        if (typeStr === "faculty") type = "researcher";
        else if (typeStr === "institution") type = "institution";
        else if (typeStr === "unit") type = "unit";
      }
    } catch (e) {
      // Ignore if not a valid URL
    }

    setUrlStatus(URL_STATUS.resolving);
    setUrlMessage("Resolving URL...");

    if (entityId && type) {
      const handleApiResult = (data, resolvedType) => {
        data = { ...data, id: entityId };
        if (data && data.name) {
          setUrlStatus(URL_STATUS.resolved);
          setUrlMessage("URL resolved successfully.");
          const fullEntity = { ...data, type: resolvedType };
          if (!localSelectedEntities.some(e => e.id === fullEntity.id)) {
            setLocalSelectedEntities(prev => [...prev, fullEntity]);
            addRecentEntity(fullEntity);
          }
          setIsDropdownOpen(false);
          setSearchValue("");
        } else {
          setUrlStatus(URL_STATUS.error);
          setUrlMessage("Could not resolve this URL. Entity not found.");
        }
      };

      if (type === "researcher") {
        getAuthorProfileSummaryByID(entityId, (data) => handleApiResult(data, "researcher"));
      } else if (type === "institution") {
        getInstitutionDetailSummaryByID(entityId, (data) => handleApiResult(data, "institution"));
      } else if (type === "unit") {
        getUnitSummaryById(entityId).then((data) => handleApiResult(data, "unit"));
      }
      return;
    }

    setUrlStatus(URL_STATUS.error);
    setUrlMessage("Invalid profile URL. Please check and try again.");
  }, [localSelectedEntities, addRecentEntity]);

  const executeSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults({ institutions: [], units: [], researchers: [] });
      setIsSearching(false);
      return;
    }

    // Check if it's a URL
    if (query.startsWith("http://") || query.startsWith("https://")) {
      handleUrlResolve(query);
      setIsSearching(false);
      return;
    }

    setUrlStatus(URL_STATUS.idle);
    setUrlMessage("");
    setIsSearching(true);

    try {
      const [instRes, unitRes, researcherRes] = await Promise.allSettled([
        searchType === "institution" ? new Promise((resolve) => getSuggestSearchInstitutions(query, resolve)) : Promise.reject(),
        searchType === "unit" ? new Promise((resolve) => getSuggestSearchUnits(query, resolve)) : Promise.reject(),
        searchType === "researcher" ? new Promise((resolve) => getSuggestSearchAuthors(query, resolve)) : Promise.reject(),
      ]);

      const institutions = instRes.status === "fulfilled" && Array.isArray(instRes.value)
        ? instRes.value.map(r => normalizeEntity(r, "institution"))
        : [];

      const units = unitRes.status === "fulfilled" && Array.isArray(unitRes.value)
        ? unitRes.value.map(r => normalizeEntity(r, "unit"))
        : [];

      const researchers = researcherRes.status === "fulfilled" && Array.isArray(researcherRes.value)
        ? researcherRes.value.map(r => normalizeEntity(r, "researcher"))
        : [];

      setSearchResults({ institutions, units, researchers });
    } catch (e) {
      console.error("Search failed", e);
    } finally {
      setIsSearching(false);
    }
  }, [normalizeEntity, handleUrlResolve, searchType]);

  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);

    if (val.length > 0) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
      setSearchResults({ institutions: [], units: [], researchers: [] });
      setUrlStatus(URL_STATUS.idle);
      setUrlMessage("");
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      executeSearch(val);
    }, 300);
  };

  const hasResults = searchResults.institutions.length > 0 || searchResults.units.length > 0 || searchResults.researchers.length > 0;
  const isUrlMode = searchValue.startsWith("http://") || searchValue.startsWith("https://");

  return (
    <div className="flex flex-col bg-white border border-gray-300 rounded-lg p-5 gap-5 shadow-sm">

      {/* Omnisearch Input */}
      <div className="flex flex-col gap-2 relative w-full" ref={searchContainerRef}>
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Find an Entity
        </span>
        <div className="flex w-full relative h-12">
          <Select value={searchType} onValueChange={setSearchType}>
            <SelectTrigger className="w-[140px] h-full rounded-r-none border-r-0 bg-gray-50 shadow-sm border-gray-300 focus:ring-1 focus:ring-gray-300 z-10 text-gray-700 font-medium">
              <SelectValue placeholder="Entity Type" />
            </SelectTrigger>
            <SelectContent>
              {allowedTypes.includes("institution") && (
                <SelectItem value="institution">Institution</SelectItem>
              )}
              {allowedTypes.includes("unit") && (
                <SelectItem value="unit">Unit</SelectItem>
              )}
              {allowedTypes.includes("researcher") && (
                <SelectItem value="researcher">Researcher</SelectItem>
              )}
            </SelectContent>
          </Select>

          <div className="relative flex-1 h-full">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center z-10">
              {isSearching || urlStatus === URL_STATUS.resolving ? (
                <Loader2 className="text-gray-400 animate-spin" size={16} />
              ) : isUrlMode ? (
                <LinkIcon className="text-gray-400" size={16} />
              ) : (
                <Search className="text-gray-400" size={16} />
              )}
            </div>

            <Input
              placeholder="Search or paste a profile URL..."
              className="w-full pl-9 h-full text-base bg-white shadow-sm border-gray-300 rounded-none focus-visible:z-20 relative"
              value={searchValue}
              onChange={handleSearchInputChange}
              onFocus={() => {
                if (searchValue.length > 0 || recentEntities.length > 0) {
                  setIsDropdownOpen(true);
                }
              }}
              disabled={false}
            />

          {searchValue && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              onClick={() => {
                setSearchValue("");
                setIsDropdownOpen(false);
                setUrlStatus(URL_STATUS.idle);
                setUrlMessage("");
                setSearchResults({ institutions: [], units: [], researchers: [] });
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>
        </div>

        {/* URL Status Message */}
        {isUrlMode && urlMessage && (
          <span className={cn(
            "text-xs mt-1",
            urlStatus === URL_STATUS.resolving ? "text-gray-500" :
              urlStatus === URL_STATUS.resolved ? "text-green-600" :
                urlStatus === URL_STATUS.error ? "text-red-600" : ""
          )}>
            {urlMessage}
          </span>
        )}

        {/* Omnisearch Dropdown */}
        {isDropdownOpen && !isUrlMode && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[400px] overflow-y-auto flex flex-col">

            {/* Show Results if typing */}
            {searchValue.length >= 2 ? (
              <>
                {isSearching && !hasResults && (
                  <div className="p-4 text-center text-sm text-gray-500">Searching...</div>
                )}

                {!isSearching && !hasResults && (
                  <div className="p-4 text-center text-sm text-gray-500">No results found for "{searchValue}"</div>
                )}

                {hasResults && (
                  <div className="py-2">
                    {searchResults.institutions.length > 0 && allowedTypes.includes("institution") && (
                      <ResultGroup title="Institutions" items={searchResults.institutions} onSelect={handleEntitySelect} />
                    )}
                    {searchResults.units.length > 0 && allowedTypes.includes("unit") && (
                      <ResultGroup title="Units" items={searchResults.units} onSelect={handleEntitySelect} />
                    )}
                    {searchResults.researchers.length > 0 && allowedTypes.includes("researcher") && (
                      <ResultGroup title="Researchers" items={searchResults.researchers} onSelect={handleEntitySelect} />
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Show Recent Searches if empty or typing < 2 chars */
              <div className="py-2">
                {recentEntities.filter(e => !e.type || allowedTypes.includes(e.type)).length > 0 ? (
                  <ResultGroup
                    title="Recent Searches"
                    items={recentEntities.filter(e => !e.type || allowedTypes.includes(e.type))}
                    onSelect={handleEntitySelect}
                  />
                ) : (
                  <div className="p-4 text-center text-sm text-gray-500 italic">Type to search for an entity...</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {localSelectedEntities && localSelectedEntities.length > 0 && <div className="h-px bg-gray-100 my-1 w-full" />}

      {/* Bottom Area: Selected Entities */}
      {localSelectedEntities && localSelectedEntities.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center mt-1">
          {localSelectedEntities.map(entity => (
            <SelectedEntityChip 
              key={entity.id} 
              entity={entity} 
              onClear={() => {
                const updated = localSelectedEntities.filter(e => e.id !== entity.id);
                setLocalSelectedEntities(updated);
              }} 
            />
          ))}
          {localSelectedEntities.length > 1 && (
            <Button variant="ghost" size="sm" onClick={() => { setLocalSelectedEntities([]); }} className="h-7 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-3 rounded-full font-medium ml-1">
              Clear All
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Subcomponents for the Dropdown and Card

const ResultGroup = ({ title, items, onSelect }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-2 last:mb-0">
      <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider bg-gray-50/50">
        {title}
      </div>
      {items.map((entity, idx) => {
        const Icon = entity.type === "institution" ? Building2 : entity.type === "unit" ? Network : User;

        let subtitle = "";
        const inst = entity.institution_name || entity.institution;
        if (inst) {
          subtitle = typeof inst === "object" ? (inst.name || "") : inst;
        }

        return (
          <div
            key={entity.id || idx}
            className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => onSelect(entity)}
          >
            <Icon size={16} className="text-gray-400 flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-gray-900 truncate">{entity.name}</span>
              {subtitle && (
                <span className="text-xs text-gray-500 truncate">{subtitle}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SelectedEntityChip = ({ entity, onClear }) => {
  const Icon = entity.type === "institution" ? Building2 : entity.type === "unit" ? Network : User;
  const imageSrc = entity.photo || entity.avatar || entity.logo || entity.image;

  return (
    <div className="flex items-center gap-2 px-1.5 py-1.5 border border-slate-200 bg-white rounded-full shadow-sm hover:border-slate-300 hover:shadow-md transition-all max-w-[260px]">
      <div className="w-6 h-6 flex-shrink-0 bg-slate-100 text-slate-500 rounded-full flex items-center justify-center overflow-hidden border border-slate-200">
        {imageSrc ? (
          <img src={imageSrc} alt={entity.name} className="w-full h-full object-contain bg-white p-0.5" />
        ) : (
          <Icon size={12} />
        )}
      </div>

      <div className="flex flex-col min-w-0 pr-1">
        <span className="text-xs font-semibold text-slate-700 truncate leading-tight">{entity.name}</span>
      </div>

      <button 
        onClick={onClear}
        className="flex-shrink-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50 p-1 rounded-full transition-colors mr-0.5"
        title="Remove"
      >
        <X size={14} />
      </button>
    </div>
  );
};

const RecentSearchesChips = ({ recentEntities, onSelect }) => (
  <div className="flex items-center gap-3 w-full overflow-x-auto pb-1 scrollbar-hide">
    <div className="flex items-center gap-1.5 flex-shrink-0 mr-2">
      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
        Recent Searches
      </span>
    </div>

    {recentEntities.length === 0 ? (
      <span className="text-xs text-gray-400 italic">No recent searches. Start by searching above.</span>
    ) : (
      <div className="flex items-center gap-2">
        {recentEntities.map((item) => {
          const Icon = item.type === "institution" ? Building2 : item.type === "unit" ? Network : User;
          const typeLabel = item.type === "institution" ? "Inst." : item.type === "unit" ? "Unit" : "Res.";

          return (
            <button
              key={item.id}
              onClick={() => onSelect(item)}
              className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-full text-xs text-gray-700 bg-white hover:border-gray-300 hover:bg-gray-50 transition-all flex-shrink-0 group"
            >
              <Icon size={12} className="text-gray-400 group-hover:text-gray-600 transition-colors" />
              <span className="font-medium max-w-[120px] truncate">{item.name}</span>
              <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider">
                {typeLabel}
              </span>
            </button>
          );
        })}
      </div>
    )}
  </div>
);

export default EntitySelector;
